import React, { useState, useEffect } from 'react';
import { 
  Building, Building2, Users, User, ChevronRight, ChevronDown,
  Loader2, AlertCircle
} from 'lucide-react';
import { cn } from '../../utils/cn';
import { tenantService } from '../../api/tenantService';
import { organizationService } from '../../api/organizationService';
import { departmentService } from '../../api/departmentService';
import { teamService } from '../../api/teamService';
import { userManagementService } from '../../api/userManagementService';

/**
 * HierarchyTree Component
 * Displays hierarchical tree view: Tenants > Organizations > Departments > Teams > Users
 * 
 * @param {Object} selectedEntity - Currently selected entity
 * @param {Function} onSelect - Callback when entity is selected
 * @param {Function} onNavigate - Callback for breadcrumb navigation
 */
const HierarchyTree = ({ selectedEntity, onSelect, onNavigate }) => {
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [loadingNodes, setLoadingNodes] = useState(new Set());
  const [error, setError] = useState(null);

  // Load initial tenants
  useEffect(() => {
    loadTenants();
  }, []);

  const loadTenants = async () => {
    try {
      setLoadingNodes(prev => new Set(prev).add('tenants'));
      const response = await tenantService.getTenants({ page: 0, size: 100 });
      const tenants = response.data?.data?.content || response.data?.data || [];
      
      const tenantNodes = tenants.map(tenant => ({
        id: `tenant-${tenant.tenantId}`,
        type: 'tenant',
        name: tenant.tenantName || tenant.name,
        data: tenant,
        children: []
      }));
      
      setTreeData(tenantNodes);
      setError(null);
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError('Failed to load tenants');
    } finally {
      setLoadingNodes(prev => {
        const next = new Set(prev);
        next.delete('tenants');
        return next;
      });
    }
  };

  const loadChildren = async (node) => {
    if (loadingNodes.has(node.id)) return;
    
    try {
      setLoadingNodes(prev => new Set(prev).add(node.id));
      
      let children = [];
      
      switch (node.type) {
        case 'tenant':
          const orgsResponse = await organizationService.getOrganizationsByTenant(node.data.tenantId);
          const orgs = orgsResponse.data?.data || [];
          children = orgs.map(org => ({
            id: `org-${org.organizationId}`,
            type: 'organization',
            name: org.organizationName || org.name,
            data: org,
            children: []
          }));
          break;
          
        case 'organization':
          const deptsResponse = await departmentService.getDepartmentsByOrganization(node.data.organizationId);
          const depts = deptsResponse.data?.data || [];
          children = depts.map(dept => ({
            id: `dept-${dept.departmentId}`,
            type: 'department',
            name: dept.departmentName || dept.name,
            data: dept,
            children: []
          }));
          break;
          
        case 'department':
          const teamsResponse = await teamService.getTeamsByDepartment(node.data.departmentId);
          const teams = teamsResponse.data?.data || [];
          children = teams.map(team => ({
            id: `team-${team.teamId}`,
            type: 'team',
            name: team.teamName || team.name,
            data: team,
            children: []
          }));
          break;
          
        case 'team':
          const membersResponse = await teamService.getTeamMembers(node.data.teamId);
          const members = membersResponse.data?.data || [];
          children = members.map(member => ({
            id: `user-${member.userId || member.id}`,
            type: 'user',
            name: member.fullName || member.username || member.name,
            data: member,
            children: []
          }));
          break;
          
        default:
          break;
      }
      
      // Update tree data with loaded children
      setTreeData(prev => updateNodeChildren(prev, node.id, children));
      setError(null);
    } catch (err) {
      console.error(`Error loading children for ${node.type}:`, err);
      setError(`Failed to load ${node.type} children`);
    } finally {
      setLoadingNodes(prev => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
    }
  };

  const updateNodeChildren = (nodes, nodeId, children) => {
    return nodes.map(node => {
      if (node.id === nodeId) {
        return { ...node, children };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: updateNodeChildren(node.children, nodeId, children) };
      }
      return node;
    });
  };

  const toggleExpand = async (node) => {
    const isExpanded = expandedNodes.has(node.id);
    
    if (isExpanded) {
      setExpandedNodes(prev => {
        const next = new Set(prev);
        next.delete(node.id);
        return next;
      });
    } else {
      setExpandedNodes(prev => new Set(prev).add(node.id));
      
      // Load children if not already loaded
      if (!node.children || node.children.length === 0) {
        await loadChildren(node);
      }
    }
  };

  const handleSelect = (node) => {
    onSelect && onSelect(node);
    
    // Build hierarchy path for breadcrumb
    const path = buildPath(node);
    onNavigate && onNavigate(path);
  };

  const buildPath = (node) => {
    const path = [];
    let current = node;
    
    while (current) {
      path.unshift({
        id: current.data?.tenantId || current.data?.organizationId || 
            current.data?.departmentId || current.data?.teamId || 
            current.data?.userId || current.id,
        type: current.type,
        name: current.name,
        ...current.data
      });
      
      // Find parent (simplified - in real implementation, track parent references)
      current = null;
    }
    
    return path;
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'tenant':
        return <Building className="w-4 h-4 text-amber-600" />;
      case 'organization':
        return <Building2 className="w-4 h-4 text-blue-600" />;
      case 'department':
        return <Users className="w-4 h-4 text-green-600" />;
      case 'team':
        return <Users className="w-4 h-4 text-purple-600" />;
      case 'user':
        return <User className="w-4 h-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const renderTreeNode = (node, level = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const isLoading = loadingNodes.has(node.id);
    const isSelected = selectedEntity?.id === node.id;
    const hasChildren = node.type !== 'user';
    const Icon = getEntityIcon(node.type);

    return (
      <div key={node.id}>
        <div
          className={cn(
            "flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
            isSelected && "bg-blue-50 border-l-2 border-blue-500"
          )}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => handleSelect(node)}
        >
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node);
              }}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              ) : isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-5" /> // Spacer for alignment
          )}
          
          {Icon && <span>{Icon}</span>}
          
          <span className={cn(
            "text-sm flex-1 truncate",
            isSelected ? "font-semibold text-blue-700" : "text-gray-700"
          )}>
            {node.name}
          </span>
        </div>
        
        {isExpanded && node.children && node.children.length > 0 && (
          <div>
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col border-r bg-white">
      <div className="px-4 py-3 border-b bg-gray-50">
        <h3 className="text-sm font-semibold text-gray-900">Organizational Hierarchy</h3>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="p-4 flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        )}
        
        {treeData.length === 0 && !error && (
          <div className="p-4 text-sm text-gray-500 text-center">
            No data available
          </div>
        )}
        
        {treeData.map(node => renderTreeNode(node))}
      </div>
    </div>
  );
};

export default HierarchyTree;

